import { Bot, Calendar, FileSpreadsheet, Loader2, Plug, RefreshCw, User, X } from 'lucide-react'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import type { Actor, ChangesetStatus } from '../lib/types'
import { Markdown } from './Markdown'

export function Spinner({ size = 14 }: { size?: number }) {
  return <Loader2 size={size} className="spin" />
}

export function Card({ children, className = '', style }: { children: ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={`card ${className}`} style={style}>
      {children}
    </div>
  )
}

export function CardHead({ title, sub, icon, right }: { title: ReactNode; sub?: ReactNode; icon?: ReactNode; right?: ReactNode }) {
  return (
    <div className="card-head">
      <div style={{ minWidth: 0 }}>
        <h3>
          {icon}
          {title}
        </h3>
        {sub && <div className="card-sub">{sub}</div>}
      </div>
      {right}
    </div>
  )
}

export function PageHead({ eyebrow, title, sub, right }: { eyebrow?: string; title: ReactNode; sub?: ReactNode; right?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {right && <div className="row wrap">{right}</div>}
    </div>
  )
}

export function Stat({ label, value, sub, tone }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: 'pos' | 'neg' }) {
  return (
    <Card className="stat">
      <div className="stat-label">{label}</div>
      <div className={`stat-value ${tone ?? ''}`}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </Card>
  )
}

export function Skeleton({ h = 16, w = '100%' }: { h?: number; w?: number | string }) {
  return <div className="skel" style={{ height: h, width: w }} />
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="col" style={{ gap: 10, padding: 20 }}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} h={14} w={`${90 - i * 12}%`} />
      ))}
    </div>
  )
}

export function ErrorBox({ error }: { error: unknown }) {
  return <div className="error-box">{error instanceof Error ? error.message : 'Something went wrong.'}</div>
}

export function Empty({ title, children, action }: { title?: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {title && <h3 style={{ marginBottom: 6 }}>{title}</h3>}
      {children && <div className="small">{children}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  )
}

const STATUS: Record<ChangesetStatus, [string, string]> = {
  draft: ['Draft', ''],
  open: ['In review', 'blue'],
  approved: ['Approved', 'accent'],
  merged: ['In the plan', 'green'],
  rejected: ['Rejected', 'red'],
  changes_requested: ['Changes requested', 'amber'],
  discarded: ['Discarded', ''],
}

export function StatusPill({ status }: { status: ChangesetStatus }) {
  const [label, tone] = STATUS[status]
  return <span className={`pill ${tone}`}>{label}</span>
}

export function SourceIcon({ source, size = 14 }: { source: string; size?: number }) {
  switch (source) {
    case 'excel':
      return <FileSpreadsheet size={size} color="var(--xl)" />
    case 'reforecast':
      return <RefreshCw size={size} color="var(--amber)" />
    case 'calendar':
      return <Calendar size={size} color="var(--muted)" />
    case 'mcp':
      return <Plug size={size} color="var(--data-ink)" />
    case 'agent':
      return <Bot size={size} color="var(--data-ink)" />
    default:
      return <User size={size} color="var(--muted)" />
  }
}

export function Author({ a }: { a: Actor }) {
  return (
    <span className="row" style={{ gap: 5 }}>
      {a.kind === 'agent' ? (
        <Bot size={13} color="var(--data-ink)" />
      ) : a.kind === 'system' ? (
        <Calendar size={13} color="var(--muted)" />
      ) : (
        <User size={13} color="var(--muted)" />
      )}
      <span>{a.name}</span>
      {a.kind === 'agent' && a.onBehalfOf && <span className="faint">for {a.onBehalfOf.name}</span>}
    </span>
  )
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: T; label: ReactNode }[]
  value: T
  onChange: (id: T) => void
}) {
  return (
    <div className="tabs">
      {tabs.map(t => (
        <button key={t.id} className={`tab ${value === t.id ? 'active' : ''}`} onClick={() => onChange(t.id)}>
          {t.label}
        </button>
      ))}
    </div>
  )
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="seg">
      {options.map(o => (
        <button key={o.id} className={value === o.id ? 'on' : ''} onClick={() => onChange(o.id)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Drawer({
  title,
  sub,
  onClose,
  children,
  wide = false,
  actions,
}: {
  title: ReactNode
  sub?: ReactNode
  onClose: () => void
  children: ReactNode
  wide?: boolean
  actions?: ReactNode
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  return (
    <>
      <div className="drawer-bg" onClick={onClose} />
      <div className={`drawer ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div style={{ minWidth: 0 }}>
            <h2>{title}</h2>
            {sub && (
              <div className="muted small" style={{ marginTop: 3 }}>
                {sub}
              </div>
            )}
          </div>
          <div className="grow" />
          {actions}
          <button className="btn ghost sm" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </div>
    </>
  )
}

export function Modal({
  title,
  sub,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode
  sub?: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** for dialogs that lay out a grid of choices */
  wide?: boolean
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])
  return (
    <div className="modal-bg" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog">
        <div className="modal-head">
          <h2>{title}</h2>
          {sub && (
            <div className="muted small" style={{ marginTop: 4 }}>
              {sub}
            </div>
          )}
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- toasts

type Toast = { id: number; title: string; sub?: string; icon?: ReactNode }
const ToastCtx = createContext<(title: string, sub?: string, icon?: ReactNode) => void>(() => {})
export const useToast = () => useContext(ToastCtx)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const toast = useCallback((title: string, sub?: string, icon?: ReactNode) => {
    const id = Date.now() + Math.random()
    setToasts(t => [...t.slice(-3), { id, title, sub, icon }])
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 5000)
  }, [])
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="toasts">
        {toasts.map(t => (
          <div className="toast fade-in" key={t.id}>
            {t.icon}
            <div>
              <div className="t-title">{t.title}</div>
              {t.sub && <div className="t-sub">{t.sub}</div>}
            </div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

// ---------------------------------------------------------------- markdown (small, safe: no HTML)

/** Markdown for agent answers, rationales and descriptions (see Markdown.tsx). */
export function Md({ text }: { text: string }) {
  return <Markdown text={text} />
}

// ---------------------------------------------------------------- code

const FN = /\b(sum_over|avg_over|last_over|broadcast|monthly|weekly|filter|rollup|lag|cumsum|place|if|min|max|abs)\b(?=\()/

function hlFormula(s: string, key: string): ReactNode[] {
  const parts: ReactNode[] = []
  const re = new RegExp(`${FN.source}|('[^']*')|(\\b\\d+(?:\\.\\d+)?\\b)|(\\b(?:and|or|not|in)\\b)`, 'g')
  let last = 0
  let i = 0
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) parts.push(s.slice(last, m.index))
    const cls = m[1] ? 'k-fn' : m[2] ? 'k-str' : m[3] ? 'k-num' : 'k-kw'
    parts.push(
      <span key={`${key}-${i++}`} className={cls}>
        {m[0]}
      </span>,
    )
    last = m.index + m[0].length
  }
  if (last < s.length) parts.push(s.slice(last))
  return parts
}

export function Formula({ text }: { text: string }) {
  return (
    <code className="spec-formula" style={{ display: 'inline-block' }}>
      {hlFormula(text, 'f')}
    </code>
  )
}

export function Yaml({ text, maxHeight }: { text: string; maxHeight?: number }) {
  let inFormula = false
  return (
    <pre className="code" style={{ maxHeight }}>
      {text
        .replace(/\n$/, '')
        .split('\n')
        .map((line, i) => {
          const com = /^(\s*)(#.*)$/.exec(line)
          if (com)
            return (
              <div key={i}>
                {com[1]}
                <span className="k-com">{com[2]}</span>
              </div>
            )
          const kv = /^(\s*-?\s*)([A-Za-z_][\w]*)(:)(\s*)(.*)$/.exec(line)
          if (kv) {
            const key = kv[2] as string
            const val = kv[5] as string
            const isF = key === 'formula' || key === 'assert' || key === 'source'
            inFormula = isF && (val === '>-' || val === '>' || val === '|')
            return (
              <div key={i}>
                {kv[1]}
                <span className="k-key">{key}</span>
                {kv[3]}
                {kv[4]}
                {isF ? (
                  hlFormula(val, `f${i}`)
                ) : /^'.*'$/.test(val) ? (
                  <span className="k-str">{val}</span>
                ) : /^-?\d+(\.\d+)?$/.test(val) ? (
                  <span className="k-num">{val}</span>
                ) : (
                  val
                )}
              </div>
            )
          }
          if (inFormula && /^\s{4,}\S/.test(line)) return <div key={i}>{hlFormula(line, `f${i}`)}</div>
          inFormula = false
          return <div key={i}>{line || ' '}</div>
        })}
    </pre>
  )
}

export function Diff({ text, maxHeight }: { text: string; maxHeight?: number }) {
  return (
    <pre className="code" style={{ maxHeight }}>
      {text.split('\n').map((l, i) => {
        const cls =
          l.startsWith('+++') || l.startsWith('---') || l.startsWith('diff') || l.startsWith('index')
            ? 'diff-meta'
            : l.startsWith('+')
              ? 'diff-add'
              : l.startsWith('-')
                ? 'diff-del'
                : l.startsWith('@@')
                  ? 'diff-hunk'
                  : ''
        return (
          <span key={i} className={cls}>
            {l || ' '}
            {!cls && '\n'}
          </span>
        )
      })}
    </pre>
  )
}

// ---------------------------------------------------------------- charts

export function DeltaBars({ rows, format }: { rows: { label: string; value: number }[]; format: (v: number) => string }) {
  const max = Math.max(1, ...rows.map(r => Math.abs(r.value)))
  return (
    <div className="col" style={{ gap: 6 }}>
      {rows.map(r => (
        <div
          key={r.label}
          style={{ display: 'grid', gridTemplateColumns: '150px 1fr 1fr 84px', alignItems: 'center', gap: 8, fontSize: 12 }}
        >
          <span className="muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.label}
          </span>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {r.value < 0 && (
              <div
                style={{
                  width: `${(Math.abs(r.value) / max) * 100}%`,
                  height: 10,
                  background: 'var(--green)',
                  borderRadius: 0,
                  opacity: 0.85,
                }}
              />
            )}
          </div>
          <div style={{ borderLeft: '1px solid var(--border-strong)', display: 'flex' }}>
            {r.value > 0 && (
              <div
                style={{
                  width: `${(r.value / max) * 100}%`,
                  height: 10,
                  background: 'var(--red)',
                  borderRadius: 0,
                  opacity: 0.8,
                }}
              />
            )}
          </div>
          <span className={`num ${r.value < 0 ? 'pos' : r.value > 0 ? 'neg' : ''}`} style={{ textAlign: 'right' }}>
            {format(r.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

export type Series = { name: string; color: string; values: (number | null)[]; dashed?: boolean }

export function LineChart({
  labels,
  series,
  height = 200,
  format,
  marker,
}: {
  labels: string[]
  series: Series[]
  height?: number
  format: (v: number) => string
  marker?: number
}) {
  const [hover, setHover] = useState<number | null>(null)
  const W = 760
  const H = height
  const pl = 60
  const pr = 12
  const pt = 12
  const pb = 26
  const all = series.flatMap(s => s.values.filter((v): v is number => v !== null))
  if (!all.length) return <Empty>No data yet.</Empty>
  let lo = Math.min(...all)
  let hi = Math.max(...all)
  const pad = (hi - lo) * 0.12 || Math.abs(hi) * 0.1 || 1
  lo = lo >= 0 ? Math.max(0, lo - pad) : lo - pad
  hi += pad
  const x = (i: number) => pl + (i * (W - pl - pr)) / Math.max(1, labels.length - 1)
  const y = (v: number) => pt + (1 - (v - lo) / (hi - lo)) * (H - pt - pb)
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => lo + t * (hi - lo))
  return (
    <svg
      role="img"
      aria-label={series.map(s => s.name).join(' vs ')}
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onMouseLeave={() => setHover(null)}
      onMouseMove={e => {
        const r = e.currentTarget.getBoundingClientRect()
        const i = Math.round(((((e.clientX - r.left) / r.width) * W - pl) / (W - pl - pr)) * (labels.length - 1))
        setHover(i >= 0 && i < labels.length ? i : null)
      }}
    >
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={pl} x2={W - pr} y1={y(t)} y2={y(t)} stroke="var(--row-line)" />
          <text x={pl - 8} y={y(t) + 4} fontSize="10.5" fill="var(--faint)" textAnchor="end">
            {format(t)}
          </text>
        </g>
      ))}
      {marker !== undefined && marker >= 0 && (
        <rect x={pl} y={pt} width={Math.max(0, x(marker) - pl)} height={H - pt - pb} fill="var(--surface-2)" />
      )}
      {labels.map((l, i) =>
        i % Math.ceil(labels.length / 12) === 0 ? (
          <text key={i} x={x(i)} y={H - 8} fontSize="10.5" fill="var(--faint)" textAnchor="middle">
            {l}
          </text>
        ) : null,
      )}
      {series.map(s => {
        let d = ''
        s.values.forEach((v, i) => {
          if (v !== null) d += `${d && s.values[i - 1] !== null ? 'L' : 'M'}${x(i)},${y(v)}`
        })
        return (
          <path
            key={s.name}
            d={d}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeDasharray={s.dashed ? '5 4' : undefined}
            strokeLinejoin="round"
          />
        )
      })}
      {hover !== null && (
        <g>
          <line x1={x(hover)} x2={x(hover)} y1={pt} y2={H - pb} stroke="var(--border-strong)" />
          {series.map(s =>
            s.values[hover] !== null ? (
              <circle key={s.name} cx={x(hover)} cy={y(s.values[hover] as number)} r={3.5} fill={s.color} />
            ) : null,
          )}
          <g transform={`translate(${Math.min(x(hover) + 8, W - 176)},${pt + 16})`}>
            <rect width="168" height={22 + series.length * 16} fill="var(--text)" opacity="0.92" />
            <text x="10" y="15" fontSize="11" fill="var(--on-text)" fontWeight="600">
              {labels[hover]}
            </text>
            {series.map((s, i) => (
              <text key={s.name} x="10" y={32 + i * 16} fontSize="11" fill="var(--on-text)" opacity="0.78">
                <tspan fill={s.color}>●</tspan> {s.name}: {s.values[hover] !== null ? format(s.values[hover] as number) : '–'}
              </text>
            ))}
          </g>
        </g>
      )}
    </svg>
  )
}

export function Legend({ items }: { items: { name: string; color: string; dashed?: boolean }[] }) {
  return (
    <div className="row small muted" style={{ gap: 14 }}>
      {items.map(i => (
        <span key={i.name} className="row" style={{ gap: 6 }}>
          <svg width="18" height="8" aria-hidden>
            <line x1="0" x2="18" y1="4" y2="4" stroke={i.color} strokeWidth="2.5" strokeDasharray={i.dashed ? '4 3' : undefined} />
          </svg>
          {i.name}
        </span>
      ))}
    </div>
  )
}

export function MiniBar({ value, max, color = 'var(--data)' }: { value: number; max: number; color?: string }) {
  return (
    <div className="bar-track">
      <div className="bar-fill" style={{ width: `${Math.max(0, Math.min(100, (value / (max || 1)) * 100))}%`, background: color }} />
    </div>
  )
}
